import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import styles from './styles.module.css';
import { GaugeData, TurbineResponse } from './types';
import { filterTurbinesBySearch, groupTurbinesByLocation } from './utils';
import TurbineIcon from '@/assets/icons/turbine.svg?react';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import CardError from '@/components/CardError';
import EmptyState from '@/components/EmptyState';
import SearchInput from '@/components/Form/searchInput';
import TurbineLocationGroup from '@/components/TurbineLocationGroup';
import { socketsEntities } from '@/constants/navigationPaths';
import { useWebSocket } from '@/context/WebSocketContext';
import { useGetTurbinesList } from '@/hooks/services/Turbines';
import { INSTALLED_PRODUCTS_VIEW_SUMMARY } from '@/solaris-lens-common-lib/src/constants/turbines';
import delay from '@/solaris-lens-common-lib/src/utils';
import {
  processTurbineData,
  transformSocketDataToGauge,
  sortByPriority
} from '@/solaris-lens-common-lib/src/utils/turbines';

function GlobalViewContainer() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [gaugeCompleteData, setGaugeCompleteData] = useState<GaugeData[]>([]);

  const {
    data: turbinesResponse,
    isLoading,
    error,
    refetch
  } = useGetTurbinesList({
    params: { view: INSTALLED_PRODUCTS_VIEW_SUMMARY }
  }) as {
    data: TurbineResponse | undefined;
    isLoading: boolean;
    error: any;
    refetch: () => void;
  };
  const { setEntityData, socketData, readyState } = useWebSocket();

  useEffect(() => {
    if (isLoading) return;

    delay(1000).then(() => {
      setEntityData({
        entityName: socketsEntities.pulseSiteSummary,
        entityId: '*'
      });
    });

    return () => {
      setEntityData({ entityName: '', entityId: '' });
    };
  }, [location.pathname, isLoading]);

  const gaugesData = useMemo(() => {
    if (!turbinesResponse?.data) return [];
    return processTurbineData(turbinesResponse.data);
  }, [turbinesResponse]);

  const filteredGaugesData = useMemo(() => {
    return filterTurbinesBySearch(gaugesData, searchQuery);
  }, [gaugesData, searchQuery]);

  const groupedTurbines = useMemo(() => {
    return groupTurbinesByLocation(gaugeCompleteData);
  }, [gaugeCompleteData]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleTurbineClick = useCallback(
    (gauge: GaugeData) => {
      navigate(
        `/turbines/${gauge.id}?model=${encodeURIComponent(gauge.subtitle)}`
      );
    },
    [navigate]
  );

  useEffect(() => {
    if (filteredGaugesData.length > 0) {
      setGaugeCompleteData(filteredGaugesData);
    } else if (!searchQuery.trim()) {
      setGaugeCompleteData([]);
    } else {
      setGaugeCompleteData([]);
    }
  }, [filteredGaugesData, searchQuery]);

  useEffect(() => {
    if (readyState !== 1) return;
    if (gaugeCompleteData?.length !== 0 && socketData?.power?.[0]) {
      const socketDataId = socketData?.power?.[0]?.id;
      const turbineData = gaugeCompleteData?.find(
        item => item.id === socketDataId
      );

      if (!turbineData) return;
      const newGaugeData = transformSocketDataToGauge(
        turbineData as GaugeData,
        socketData?.power?.[0]
      );
      const replacedGaugeData = gaugeCompleteData.map(item =>
        item.id === newGaugeData.id ? newGaugeData : item
      );
      const sortedGaugeData = sortByPriority(replacedGaugeData);
      setGaugeCompleteData(sortedGaugeData);
    }
  }, [socketData, gaugeCompleteData?.length, readyState]);

  if (isLoading) {
    return (
      <div className={styles.mainContainer}>
        <ContainerLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.mainContainer}>
        <CardError
          title={error?.title || 'Error'}
          errorMessage={error?.message || 'Something went wrong'}
          onClick={() => refetch()}
        />
      </div>
    );
  }

  if (!gaugesData.length) {
    return (
      <div className={styles.mainContainer}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <TurbineIcon className={styles.turbineIcon} />
            <h1 className={styles.title}>Turbines</h1>
          </div>
          <div className={styles.searchSection}>
            <SearchInput
              placeholder="Search by turbine name by location or by city"
              value={searchQuery}
              onChange={handleSearchChange}
              customClass={styles.searchContainer}
            />
          </div>
        </div>
        <EmptyState
          title={`It seems you don't have any
            Turbine assigned yet.`}
          subtitle="Please contact your administrator."
        />
      </div>
    );
  }

  return (
    <div className={styles.mainContainer}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <TurbineIcon className={styles.turbineIcon} />
          <h1 className={styles.title}>Turbines</h1>
        </div>
        <div className={styles.searchSection}>
          <SearchInput
            placeholder="Search by turbine name by location or by city"
            value={searchQuery}
            onChange={handleSearchChange}
            customClass={styles.searchContainer}
          />
        </div>
      </div>

      {groupedTurbines.length === 0 && searchQuery ? (
        <EmptyState
          title="No turbines found"
          subtitle={`No results found for "${searchQuery}". Try a different search term.`}
          icon={false}
        />
      ) : (
        <div className={styles.groupsContainer}>
          {groupedTurbines.map(group => (
            <TurbineLocationGroup
              key={group.groupKey}
              group={group}
              onTurbineClick={handleTurbineClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default GlobalViewContainer;
